import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { auditLoginService } from "@/services";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Search, CheckCircle2, XCircle, Users, Activity } from "lucide-react";

interface AuditLogin {
  id: string;
  created: string;
  email: string;
  success: boolean;
  ip_address: string | null;
  user_agent: string | null;
  failure_reason: string | null;
}

type StatusFilter = "all" | "success" | "failed";
type DateFilter = "7days" | "30days" | "90days" | "all";

export function LoginHistoryTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("7days");

  // Calculate date range based on filter
  const dateRangeStart = useMemo(() => {
    const now = new Date();
    switch (dateFilter) {
      case "7days":
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case "30days":
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case "90days":
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      default:
        return null;
    }
  }, [dateFilter]);

  // Fetch login history
  const { data: loginHistory, isLoading } = useQuery({
    queryKey: ["audit-login", statusFilter, dateFilter, searchQuery],
    queryFn: async () => {
      const result = await auditLoginService.list({
        dateRangeStart: dateRangeStart ?? undefined,
        success:
          statusFilter === "success"
            ? "true"
            : statusFilter === "failed"
            ? "false"
            : undefined,
        email: searchQuery.trim() || undefined,
        limit: 500,
      });
      if (result.error) throw result.error;
      return result.data as AuditLogin[];
    },
  });

  // Calculate stats
  const stats = useMemo(() => {
    if (!loginHistory) {
      return { total: 0, successful: 0, failed: 0, uniqueUsers: 0 };
    }

    const successful = loginHistory.filter((l) => l.success).length;
    const failed = loginHistory.filter((l) => !l.success).length;
    const uniqueEmails = new Set(loginHistory.map((l) => l.email)).size;

    return {
      total: loginHistory.length,
      successful,
      failed,
      uniqueUsers: uniqueEmails,
    };
  }, [loginHistory]);

  // Truncate user agent for display
  const truncateUserAgent = (ua: string | null): string => {
    if (!ua) return "—";
    if (ua.length <= 50) return ua;
    return ua.substring(0, 50) + "...";
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logins</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.total}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.successful}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.failed}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Skeleton className="h-8 w-16" /> : stats.uniqueUsers}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={dateFilter}
          onValueChange={(value) => setDateFilter(value as DateFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7days">Last 7 days</SelectItem>
            <SelectItem value="30days">Last 30 days</SelectItem>
            <SelectItem value="90days">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="success">Successful</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        {(searchQuery || statusFilter !== "all" || dateFilter !== "7days") && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
              setDateFilter("7days");
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Login History Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date/Time</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="hidden lg:table-cell">User Agent</TableHead>
                <TableHead>Failure Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="hidden lg:table-cell"><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  </TableRow>
                ))
              ) : loginHistory && loginHistory.length > 0 ? (
                loginHistory.map((login) => (
                  <TableRow key={login.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(login.created), "MMM d, yyyy h:mm a")}
                    </TableCell>
                    <TableCell>{login.email}</TableCell>
                    <TableCell>
                      <Badge variant={login.success ? "default" : "destructive"}>
                        {login.success ? "Success" : "Failed"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {login.ip_address || "—"}
                    </TableCell>
                    <TableCell
                      className="hidden lg:table-cell text-sm text-muted-foreground max-w-xs truncate"
                      title={login.user_agent || undefined}
                    >
                      {truncateUserAgent(login.user_agent)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {login.failure_reason || "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No login records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {loginHistory && loginHistory.length >= 500 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing the most recent 500 records. Use filters to narrow down results.
        </p>
      )}
    </div>
  );
}
